/**
 * The maps of the GDD (section 10), in its order: four generated from the seed (a relief profile, then biomes)
 * and three drawn at 1024 × 512 with their own rules and events. Then the author's: the Palais des glaces, the Pont de bois, the Dôme de vapeur, the Bateau pirate, the Centrale nucléaire.
 */
import type { World } from '@invaders/sim';
import type { MapState } from '../state';
import { gen, type Gen } from './common';
export { shares } from './common';
import { generateValley } from './valley';
import { generateRefinery } from './refinery';
import { generateGlacier } from './glacier';
import { generateForest } from './forest';
import { drawFort } from './fort';
import { drawEmber } from './ember';
import { drawMoon } from './moon';
import { generatePalace } from './palace';
import { generateBridge } from './bridge';
import { drawDome } from './dome';
import { drawPirate } from './pirate';
import { drawReactor } from './reactor';
export { DOME_POOLS, DOME_DEPTH } from './dome';
export { REACTOR_BELT, REACTOR_LEVELS } from './reactor';
export { KEG_H } from './pirate';
export { BELT_BARRELS, BELT_LOAD } from './events';
export { palaceLayout } from './palace';

export { erupt, tipCauldron, mapTurnStart, updateMapEvents, CAULDRON_OIL, MAGAZINE_RADIUS, MAGAZINE_DAMAGE, LAVA_BOMBS, ERUPTION_OVERFLOW } from './events';
export { FORT_CAULDRON, FORT_GATE, FORT_MAGAZINE } from './fort';
export { ERUPTION_EVERY } from './ember';

export const MAP_IDS = ['valley', 'refinery', 'glacier', 'forest', 'fort', 'ember', 'moon', 'palace', 'bridge', 'dome', 'pirate', 'reactor'] as const;
export type MapId = (typeof MAP_IDS)[number];

export interface MapDef {
  readonly id: MapId;
  readonly name: string;
  /** One line for the lobby: what the map is about (GDD table). */
  readonly blurb: string;
  /** Drawn once and for all (the seed only changes the match, not the map). */
  readonly drawn: boolean;
  /** The Moon: no atmosphere (the sim's fire and steam rules, and MapState.airless). */
  readonly airless: boolean;
  /** Fills the empty world and returns the map's state. */
  readonly build: (g: Gen) => MapState;
}

export const MAPS: readonly MapDef[] = [
  { id: 'valley', name: 'Lake Valley', blurb: 'Dunes, a central lake, a glacier, a floating island', drawn: false, airless: false, build: generateValley },
  { id: 'refinery', name: 'Sunken Refinery', blurb: 'Tunnels, pockets of oil, water at the bottom', drawn: false, airless: false, build: generateRefinery },
  { id: 'glacier', name: 'Split Glacier', blurb: 'Ice shelves on the sea, rocky islets', drawn: false, airless: false, build: generateGlacier },
  { id: 'forest', name: 'Dry Forest', blurb: 'Sand, a dense forest, buried powder', drawn: false, airless: false, build: generateForest },
  { id: 'fort', name: 'Fort Pierre', blurb: 'A castle: moat, cauldron, powder magazine', drawn: true, airless: false, build: drawFort },
  { id: 'ember', name: 'Ember Island', blurb: 'An active volcano: it erupts every 4 turns', drawn: true, airless: false, build: drawEmber },
  { id: 'moon', name: 'Sea of Tranquility', blurb: 'The Moon: gravity / 3, no air, no wind', drawn: true, airless: true, build: drawMoon },
  { id: 'palace', name: 'Ice Palace', blurb: 'A maze of ice: corridors and mirrors', drawn: false, airless: false, build: generatePalace },
  { id: 'bridge', name: 'Timber Bridge', blurb: 'A wooden bridge over the sea: burn it and they fall', drawn: false, airless: false, build: generateBridge },
  { id: 'dome', name: 'Steam Dome', blurb: 'Hot pools boil, an ice roof catches the steam and rains it back', drawn: true, airless: false, build: drawDome },
  { id: 'pirate', name: 'Pirate Ship', blurb: 'A galleon of wood full of black powder kegs', drawn: true, airless: false, build: drawPirate },
  { id: 'reactor', name: 'Nuclear Plant', blurb: 'Four levels, a reactor, a conveyor that brings acid barrels', drawn: true, airless: false, build: drawReactor },
];

export function isMapId(id: string): id is MapId {
  return (MAP_IDS as readonly string[]).includes(id);
}

export function mapDef(id: MapId): MapDef {
  return MAPS[MAP_IDS.indexOf(id)];
}

/** Builds map `id` into `world` (created with `airless` as mapDef(id) says), ends with every chunk awake. */
export function generateMap(world: World, id: MapId, seed: number): MapState {
  return mapDef(id).build(gen(world, seed));
}
